/**
 * Service: SpeechToTextService
 * SpecReferenceID: SPEECH-V1
 * Requirements: SPEECH-V1-R03
 *
 * Speech-to-Text service for transcribing audio to normalized text
 * using a configured STT provider (e.g., Azure Speech Services, Whisper).
 *
 * SPEECH-V1-R03: System shall transcribe audio to normalized text using
 *                configured STT provider.
 */

import api from '@/lib/api';
import type { AudioData } from '@/hooks/use-audio-capture';

/**
 * Transcription result from STT provider.
 */
export interface TranscriptionResult {
  /** Transcribed text output, normalized and cleaned */
  text: string;
  /** Confidence score (0-1) from the STT provider */
  confidence: number;
  /** Audio duration in seconds */
  duration: number;
  /** Detected language code (e.g., 'en-US') */
  language?: string;
  /** Whether the transcription is final or partial */
  isFinal: boolean;
}

/**
 * STT provider types.
 */
export type STTProviderType = 'azure' | 'whisper' | 'mock';

/**
 * Configuration for Speech-to-Text service.
 * Depends on CONFIG-V1 for runtime configuration.
 */
export interface SpeechToTextConfig {
  /** STT provider type */
  provider: STTProviderType;
  /** Provider endpoint URL (optional, uses default if not provided) */
  endpoint?: string;
  /** Confidence threshold below which transcription is flagged */
  confidenceThreshold: number;
  /** Maximum audio duration in seconds */
  maxDuration: number;
  /** Preferred language for transcription */
  language?: string;
}

/**
 * Default configuration values.
 */
const DEFAULT_CONFIG: SpeechToTextConfig = {
  provider: 'azure',
  confidenceThreshold: 0.7,
  maxDuration: 120,
  language: 'en-US',
};

/**
 * API response type from backend STT endpoint.
 */
interface STTApiResponse {
  text: string;
  confidence: number;
  duration: number;
  language: string;
  is_final: boolean;
}

/**
 * Error class for STT-specific errors.
 */
export class SpeechToTextError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly retryable: boolean = false
  ) {
    super(message);
    this.name = 'SpeechToTextError';
  }
}

/**
 * Speech-to-Text service class.
 *
 * SPEECH-V1-R03: Transcribe audio to normalized text using configured STT provider.
 */
export class SpeechToTextService {
  private config: SpeechToTextConfig;
  private maxRetries: number;

  constructor(config: Partial<SpeechToTextConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.maxRetries = 3;
  }

  /**
   * Transcribe audio data to text.
   *
   * @param audioData - Audio data from useAudioCapture hook
   * @returns Transcription result with normalized text
   * @throws SpeechToTextError if transcription fails
   */
  async transcribe(
    audioData: AudioData,
    language?: string
  ): Promise<TranscriptionResult> {
    // Validate audio duration
    if (audioData.duration > this.config.maxDuration) {
      throw new SpeechToTextError(
        `Audio duration (${audioData.duration}s) exceeds maximum (${this.config.maxDuration}s)`,
        'DURATION_EXCEEDED',
        false
      );
    }

    // Create form data for multipart upload
    const formData = new FormData();
    formData.append('audio', audioData.blob, 'audio.' + this.getFileExtension(audioData.mimeType));
    formData.append('provider', this.config.provider);
    // SPEECH-V1-R14: a per-call language (the user's declared choice)
    // takes precedence over the construction-time default. Without this
    // the non-streaming path always transcribed as the configured
    // default regardless of what the user selected - the same defect the
    // streaming path had.
    const effectiveLanguage = language ?? this.config.language;
    if (effectiveLanguage) {
      formData.append('language', effectiveLanguage);
    }

    // Execute with retry logic
    let lastError: Error | null = null;
    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        const result = await this.executeTranscription(formData);
        return result;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // Check if error is retryable
        if (error instanceof SpeechToTextError && !error.retryable) {
          throw error;
        }

        // Wait before retry (exponential backoff)
        if (attempt < this.maxRetries) {
          await this.delay(Math.pow(2, attempt) * 500);
        }
      }
    }

    throw lastError ?? new SpeechToTextError('Transcription failed', 'UNKNOWN_ERROR', false);
  }

  /**
   * Execute the transcription API call.
   */
  private async executeTranscription(formData: FormData): Promise<TranscriptionResult> {
    try {
      const response = await api.post<STTApiResponse>('/speech/transcribe', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 second timeout for transcription
      });

      const data = response.data;

      // Normalize the response
      const result: TranscriptionResult = {
        text: this.normalizeText(data.text),
        confidence: data.confidence,
        duration: data.duration,
        language: data.language,
        isFinal: data.is_final,
      };

      return result;
    } catch (error) {
      // Convert axios errors to SpeechToTextError
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number; data?: { message?: string; code?: string } } };
        const status = axiosError.response?.status;
        const message = axiosError.response?.data?.message ?? 'Transcription request failed';
        const code = axiosError.response?.data?.code ?? 'API_ERROR';

        // Determine if retryable based on status code
        const retryable = status === 429 || (status !== undefined && status >= 500);

        throw new SpeechToTextError(message, code, retryable);
      }

      throw new SpeechToTextError('Network error during transcription', 'NETWORK_ERROR', true);
    }
  }

  /**
   * Normalize transcribed text.
   * Removes extra whitespace and non-speech noise markers.
   */
  private normalizeText(text: string): string {
    return text
      .replace(/\[.*?\]/g, '') // Remove noise markers like [NOISE], [MUSIC]
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
  }

  /**
   * Get file extension from MIME type.
   */
  private getFileExtension(mimeType: string): string {
    const extensionMap: Record<string, string> = {
      'audio/wav': 'wav',
      'audio/wave': 'wav',
      'audio/x-wav': 'wav',
      'audio/mp3': 'mp3',
      'audio/mpeg': 'mp3',
      'audio/webm': 'webm',
      'audio/ogg': 'ogg',
    };

    return extensionMap[mimeType] ?? 'audio';
  }

  /**
   * Delay utility for retry backoff.
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Check if transcription confidence is below threshold.
   */
  isLowConfidence(result: TranscriptionResult): boolean {
    return result.confidence < this.config.confidenceThreshold;
  }

  /**
   * Update service configuration.
   */
  updateConfig(config: Partial<SpeechToTextConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration.
   */
  getConfig(): SpeechToTextConfig {
    return { ...this.config };
  }
}

/**
 * Default service instance.
 */
export const speechToTextService = new SpeechToTextService();

export default SpeechToTextService;
